import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Eye, Lock, FileText, Users, Mail } from 'lucide-react';
import SEO from '../components/SEO';

const Privacy = () => {
  const sections = [
    {
      icon: <Eye className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Information We Collect',
      content: [
        'Personal information you provide when contacting us (name, email, phone number)',
        'Information about your medical equipment needs and preferences',
        'Technical data such as browser type and IP address when you visit our website',
        'Communication records when you interact with our customer service team'
      ]
    },
    {
      icon: <FileText className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'How We Use Your Information',
      content: [
        'To respond to your inquiries and provide customer support',
        'To process and fulfill your equipment requests',
        'To send you important updates about your orders or our services',
        'To improve our website and services based on your feedback',
        'To comply with legal obligations and protect our rights'
      ]
    },
    {
      icon: <Users className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Information Sharing',
      content: [
        'We do not sell, trade, or rent your personal information to third parties',
        'We may share information with trusted service providers who assist in our operations',
        'We may disclose information when required by law or to protect our rights',
        'Insurance-related information may be shared with your insurance provider with your consent'
      ]
    },
    {
      icon: <Lock className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Data Security',
      content: [
        'We implement industry-standard security measures to protect your data',
        'Access to personal information is restricted to authorized personnel only',
        'We use secure connections (SSL/TLS) for data transmission',
        'Regular security assessments are conducted to ensure data protection'
      ]
    },
    {
      icon: <Shield className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Your Rights',
      content: [
        'You have the right to access the personal information we hold about you',
        'You may request correction of inaccurate or incomplete information',
        'You can request deletion of your personal data, subject to legal requirements',
        'You may opt out of marketing communications at any time'
      ]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="privacy-page">
      <SEO 
        title="Privacy Policy | Kalorama Medical"
        description="Learn how Kalorama Medical collects, uses, and protects your personal information. Your privacy and data security are our priority."
        keywords="privacy policy, data protection, personal information, medical equipment privacy"
      />
      
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6 text-[#7bb5d4]" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
        </div>
      </div>

      {/* Last Updated */}
      <div className="bg-blue-50 py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 text-sm">Last Updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <Card className="mb-8 border-2 border-blue-100">
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed">
                  Kalorama Medical ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                  describes how we collect, use, disclose, and safeguard your information when you visit our website 
                  or use our services. Please read this policy carefully. By using our services, you consent to the 
                  practices described in this Privacy Policy.
                </p>
              </CardContent>
            </Card>

            {/* Sections */}
            <div className="space-y-6">
              {sections.map((section, index) => (
                <Card key={index} className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mr-4">
                        {section.icon}
                      </div>
                      <h2 className="text-2xl font-bold text-[#1e3a5f]">{section.title}</h2>
                    </div>
                    <ul className="space-y-3">
                      {section.content.map((item, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="text-[#7bb5d4] mr-3 mt-1.5 text-sm">&#9679;</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Contact for Privacy */}
            <Card className="mt-8 bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white">
              <CardContent className="p-8 text-center">
                <Mail className="h-10 w-10 mx-auto mb-4 text-[#7bb5d4]" />
                <h3 className="text-xl font-semibold mb-2">Questions About Privacy?</h3>
                <p className="text-blue-100 mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us.
                </p>
                <a 
                  href="mailto:privacy@kaloramamedical.com" 
                  className="text-[#7bb5d4] hover:text-white transition-colors duration-200 font-medium"
                >
                  privacy@kaloramamedical.com
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
